import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type {
  Classnames,
  Maybe,
  PriceAlignment,
  PriceNode,
  PriceTheme,
  TypoColor,
  TypoSize,
  TypoWeight,
} from '@snowit/icetypes';

import useA11y from '@snowit/hooks/useA11y';
import { Currency } from '@snowit/icetypes';
import useLanguage from '@snowit/system/useLanguage';
import { toMoney, validators } from '@snowit/utils';
import testId from '@snowit/utils/testId';

import BadgeLabel from '../Badges/BadgeLabel';
import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Price.module.scss';

export type PriceStyle = {
  size?: TypoSize;
  weight?: TypoWeight;
  color?: TypoColor;
};

interface IPrice {
  theme?: PriceTheme;
  alignment?: PriceAlignment;
  className?: string;
  price?: PriceNode;
  costStyle?: PriceStyle;
  symbolStyle?: PriceStyle;
  unitStyle?: PriceStyle;
  hintStyle?: PriceStyle;
  test?: string;
  // decimals?: number;
  // delta?: boolean;
  // free?: boolean;
  child?: () => ReactNode;
  // -----------------
  classNames?: Classnames<
    'wrapper' | 'price' | 'cost' | 'symbol' | 'discount' | 'hint' | 'unit' | 'badge'
  >;
}

const themes: {
  [x in PriceTheme]: {
    cost: {
      size: TypoSize;
      weight: TypoWeight;
    };
    symbol: {
      size: TypoSize;
      weight: TypoWeight;
    };
    unit: {
      size: TypoSize;
      weight: TypoWeight;
    };
    hint: {
      size: TypoSize;
      weight: TypoWeight;
    };
  };
} = {
  vertical: {
    cost: {
      size: 'lg',
      weight: 'medium',
    },
    symbol: {
      size: 'lg',
      weight: 'regular',
    },
    unit: {
      size: 'sm',
      weight: 'regular',
    },
    hint: {
      size: 'sm',
      weight: 'regular',
    },
  },
  horizontal: {
    cost: {
      size: 'md',
      weight: 'medium',
    },
    symbol: {
      size: 'md',
      weight: 'regular',
    },
    unit: {
      size: 'md',
      weight: 'regular',
    },
    hint: {
      size: 'md',
      weight: 'regular',
    },
  },
  option: {
    cost: {
      size: 'df',
      weight: 'regular',
    },
    symbol: {
      size: 'df',
      weight: 'regular',
    },
    unit: {
      size: 'xxs',
      weight: 'regular',
    },
    hint: {
      size: 'xxs',
      weight: 'regular',
    },
  },
};

const Price = (props: IPrice) => {
  const {
    className,
    classNames = {},
    // decimals = 2, // ------
    // delta = false, // ------
    price,
    costStyle,
    symbolStyle,
    unitStyle,
    hintStyle,
    test,
    theme = 'vertical',
    alignment = 'left',
    child,
  } = props;
  const { t } = useLanguage();
  const a11yH = useA11y();

  const hintId = a11yH.makeId('hint');

  const hasDiscount = useMemo(() => {
    if (!price) return false;
    return (price.discount ?? 0) > 0;
  }, [price?.discount, price?.total]);

  const pricify = (input: number, params = {}) => {
    const currency = price?.currency || Currency.Eur;
    const symbol = toMoney.symbol(currency, price?.isSnowCoin);
    const unit = price?.unit;
    const cost = toMoney(input, {
      ...params,
      decimals: price?.isSnowCoin ? 0 : (price?.decimals ?? 2),
      isDelta: Boolean(price?.isDelta),
      symbol: false,
    });
    return (
      <div
        className={cx(s.price, classNames.price)}
        aria-describedby={hintId}
        {...testId(`${test}_price`)}
      >
        <Typo
          className={cx(s.cost, classNames.cost)}
          text={cost}
          tag={'span'}
          size={costStyle?.size || themes[theme].cost.size}
          weight={costStyle?.weight || themes[theme].cost.weight}
          color={costStyle?.color || 'text'}
        />
        <Typo
          className={cx(s.symbol, classNames.symbol)}
          text={symbol}
          tag={'span'}
          size={symbolStyle?.size || themes[theme].symbol.size}
          weight={symbolStyle?.weight || themes[theme].symbol.weight}
          color={symbolStyle?.color || 'text'}
        />
        {unit && (
          <Typo
            className={cx(s.unit, classNames.unit)}
            text={unit}
            tag={'span'}
            size={unitStyle?.size || themes[theme].unit.size}
            weight={unitStyle?.weight || themes[theme].unit.weight}
            color={unitStyle?.color || 'text-l'}
          />
        )}
      </div>
    );
  };

  const renderDiscountValue = (value?: Maybe<number>) => {
    if (!value || price?.hideDiscount) return null;
    return (
      <Typo
        text={`${Math.round(value)}%`}
        tag={'span'}
        size={hintStyle?.size || themes[theme].hint.size}
        weight={hintStyle?.weight || themes[theme].hint.weight}
        color={hintStyle?.color || 'text-l'}
      />
    );
  };

  const renderDiscount = () => {
    if (!hasDiscount || !price) return null;
    if (!price.original) return null;
    return (
      <div className={cx(s.discount, classNames.discount)}>
        <Typo
          className={s.fullPrice}
          text={toMoney(price.original, {
            decimals: price?.decimals ?? 2,
          })}
          tag={'span'}
          size={hintStyle?.size || themes[theme].hint.size}
          weight={hintStyle?.weight || themes[theme].hint.weight}
          color={hintStyle?.color || 'text-l'}
        />
        {renderDiscountValue(price.discount)}
      </div>
    );
  };

  const renderHint = () => {
    if (!price) return null;
    if (!price.total && price.hideZero) return null;
    if (!price.total && price.showBadge) return null;
    const hint = (Array.isArray(price.hint) ? price.hint : [price.hint]).filter((h): h is string =>
      Boolean(h),
    );
    return (
      <div className={cx(s.hint, classNames.hint)} id={hintId}>
        {renderDiscount()}
        {hint.map((h, i) => (
          <Typo
            key={i}
            text={h}
            tag={'span'}
            size={hintStyle?.size || themes[theme].hint.size}
            weight={hintStyle?.weight || themes[theme].hint.weight}
            color={hintStyle?.color || 'text-l'}
          />
        ))}
      </div>
    );
  };

  const renderPrice = () => {
    if (!price) return null;
    if (!price.total && price.hideZero) return null;
    if (!price.total && price.showBadge) {
      return (
        <BadgeLabel
          className={cx(s.badge, classNames.badge)}
          label={t('lzy.badge.included')}
          theme={'color'}
          bgColor={'var(--c-ok-l)'}
          labelColor={'var(--c-ok-d)'}
        />
      );
    }

    return pricify(price.total ?? 0);
  };

  if (!price) return null;
  if (price.total === null || !validators.isNumber(price.total as number)) return null;

  return (
    <div
      className={cx(s.wrapPrice, className, classNames.wrapper, {
        [s[theme]]: true,
        [s[`${alignment}Align`]]: true,
      })}
    >
      {renderPrice()}
      {renderHint()}
      {child ? child() : null}
    </div>
  );
};

export default Price;
